import React, { useState, useEffect } from 'react';
import { COLORS } from '../../../core/constants/colors';
import { Company } from '../types';

interface EditProfileDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  company: Company | null;
  onSave: (data: Partial<Company>) => Promise<void>;
  isSaving?: boolean;
}

export const EditProfileDrawer: React.FC<EditProfileDrawerProps> = ({
  isOpen,
  onClose,
  company,
  onSave,
  isSaving = false,
}) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    website: '',
    founding_year: '',
    employees: '',
    industry: '',
    location: '',
  });

  useEffect(() => {
    if (company) {
      setFormData({
        name: company.name || '',
        description: company.description || '',
        website: company.website || '',
        founding_year: company.founding_year?.toString() || '',
        employees: company.employees || '',
        industry: company.industry || '',
        location: company.location || '',
      });
    }
  }, [company, isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!company) return;

    const updateData: Partial<Company> = {};
    if (formData.name !== company.name) updateData.name = formData.name;
    if (formData.description !== company.description) updateData.description = formData.description;
    if (formData.website !== company.website) updateData.website = formData.website;
    if (formData.founding_year !== company.founding_year?.toString()) {
      updateData.founding_year = parseInt(formData.founding_year) || undefined;
    }
    if (formData.employees !== company.employees) updateData.employees = formData.employees;
    if (formData.industry !== company.industry) updateData.industry = formData.industry;
    if (formData.location !== company.location) updateData.location = formData.location;

    if (Object.keys(updateData).length > 0) {
      await onSave(updateData);
    }
  };

  const handleClose = () => {
    if (company) {
      setFormData({
        name: company.name || '',
        description: company.description || '',
        website: company.website || '',
        founding_year: company.founding_year?.toString() || '',
        employees: company.employees || '',
        industry: company.industry || '',
        location: company.location || '',
      });
    }
    onClose();
  };

  if (!isOpen || !company) return null;

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 z-50 transition-opacity"
        onClick={handleClose}
      />
      
      {/* Side Drawer */}
      <div className="fixed right-0 top-0 h-full w-full max-w-md bg-white shadow-xl z-50 transform transition-transform duration-300 ease-in-out overflow-y-auto">
        <div className="h-full flex flex-col">
          {/* Header */}
          <div className="px-6 py-5 border-b flex items-center justify-between flex-shrink-0" style={{ borderColor: '#E5E7EB' }}>
            <h2 className="text-xl font-bold" style={{ color: '#1F2937' }}>
              Edit Profile
            </h2>
            <button
              onClick={handleClose}
              className="w-8 h-8 rounded-lg flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-all"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Form Content */}
          <form onSubmit={handleSubmit} className="flex-1 px-6 py-6 overflow-y-auto space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: '#1F2937' }}>
                Company Name *
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full px-4 py-2.5 rounded-lg border focus:outline-none focus:ring-2 text-sm"
                style={{ borderColor: '#E5E7EB' }}
                onFocus={(e) => {
                  e.target.style.borderColor = COLORS.user.queryButton;
                  e.target.style.boxShadow = `0 0 0 2px ${COLORS.user.queryButton}40`;
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = '#E5E7EB';
                  e.target.style.boxShadow = 'none';
                }}
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: '#1F2937' }}>
                Description
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={4}
                className="w-full px-4 py-2.5 rounded-lg border focus:outline-none focus:ring-2 text-sm resize-none"
                style={{ borderColor: '#E5E7EB' }}
                onFocus={(e) => {
                  e.target.style.borderColor = COLORS.user.queryButton;
                  e.target.style.boxShadow = `0 0 0 2px ${COLORS.user.queryButton}40`;
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = '#E5E7EB';
                  e.target.style.boxShadow = 'none';
                }}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: '#1F2937' }}>
                  Industry
                </label>
                <input
                  type="text"
                  value={formData.industry}
                  onChange={(e) => setFormData({ ...formData, industry: e.target.value })}
                  className="w-full px-4 py-2.5 rounded-lg border focus:outline-none focus:ring-2 text-sm"
                  style={{ borderColor: '#E5E7EB' }}
                  onFocus={(e) => {
                    e.target.style.borderColor = COLORS.user.queryButton;
                    e.target.style.boxShadow = `0 0 0 2px ${COLORS.user.queryButton}40`;
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = '#E5E7EB';
                    e.target.style.boxShadow = 'none';
                  }}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: '#1F2937' }}>
                  Location
                </label>
                <input
                  type="text"
                  value={formData.location}
                  onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                  className="w-full px-4 py-2.5 rounded-lg border focus:outline-none focus:ring-2 text-sm"
                  style={{ borderColor: '#E5E7EB' }}
                  onFocus={(e) => {
                    e.target.style.borderColor = COLORS.user.queryButton;
                    e.target.style.boxShadow = `0 0 0 2px ${COLORS.user.queryButton}40`;
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = '#E5E7EB';
                    e.target.style.boxShadow = 'none';
                  }}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: '#1F2937' }}>
                  Website
                </label>
                <input
                  type="text"
                  value={formData.website}
                  onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                  className="w-full px-4 py-2.5 rounded-lg border focus:outline-none focus:ring-2 text-sm"
                  style={{ borderColor: '#E5E7EB' }}
                  onFocus={(e) => {
                    e.target.style.borderColor = COLORS.user.queryButton;
                    e.target.style.boxShadow = `0 0 0 2px ${COLORS.user.queryButton}40`;
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = '#E5E7EB';
                    e.target.style.boxShadow = 'none';
                  }}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: '#1F2937' }}>
                  Founding Year
                </label>
                <input
                  type="number"
                  value={formData.founding_year}
                  onChange={(e) => setFormData({ ...formData, founding_year: e.target.value })}
                  className="w-full px-4 py-2.5 rounded-lg border focus:outline-none focus:ring-2 text-sm"
                  style={{ borderColor: '#E5E7EB' }}
                  onFocus={(e) => {
                    e.target.style.borderColor = COLORS.user.queryButton;
                    e.target.style.boxShadow = `0 0 0 2px ${COLORS.user.queryButton}40`;
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = '#E5E7EB';
                    e.target.style.boxShadow = 'none';
                  }}
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: '#1F2937' }}>
                Employees
              </label>
              <input
                type="text"
                value={formData.employees}
                onChange={(e) => setFormData({ ...formData, employees: e.target.value })}
                placeholder="e.g., 1-10, 10-50"
                className="w-full px-4 py-2.5 rounded-lg border focus:outline-none focus:ring-2 text-sm"
                style={{ borderColor: '#E5E7EB' }}
                onFocus={(e) => {
                  e.target.style.borderColor = COLORS.user.queryButton;
                  e.target.style.boxShadow = `0 0 0 2px ${COLORS.user.queryButton}40`;
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = '#E5E7EB';
                  e.target.style.boxShadow = 'none';
                }}
              />
            </div>
          </form>

          {/* Footer */}
          <div className="px-6 py-4 border-t flex items-center gap-3 flex-shrink-0" style={{ borderColor: '#E5E7EB' }}>
            <button
              type="button"
              onClick={handleClose}
              className="flex-1 py-2.5 px-4 rounded-lg font-medium transition-all"
              style={{ border: '1px solid #E5E7EB', color: '#1F2937' }}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={isSaving}
              className="flex-1 py-2.5 px-4 rounded-lg font-medium text-white transition-all disabled:opacity-50"
              style={{ backgroundColor: COLORS.user.queryButton }}
            >
              {isSaving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

